// src/modules/notifications/pages/NotificationsPage.jsx
//
// The full inbox. The dropdown is for "what just happened"; this is for "where
// is that thing from last Tuesday" — which the old bell had no answer for at
// all, since it capped at 50 rows with no pagination and no filters.
//
// Filtering is server-side (category, unread, search) so it works across the
// whole history rather than only the page already loaded.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card, List, Typography, Input, Select, Segmented, Button, Space, Empty,
  Skeleton, Tag, Tooltip, Popconfirm, message as antdMessage, theme,
} from 'antd';
import { CheckOutlined, DeleteOutlined, ReloadOutlined, SettingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../NotificationContext';
import { fetchNotifications } from '../api';
import {
  NotificationIcon, relativeTime, dayBucket, severityColor, CATEGORY_LABEL,
} from '../registry';

const { Title, Text } = Typography;
const PAGE_SIZE = 30;

const CATEGORY_OPTIONS = [
  { value: '', label: 'All categories' },
  ...Object.entries(CATEGORY_LABEL).map(([value, label]) => ({ value, label })),
];

const NotificationsPage = () => {
  const { unread, byCategory, markRead, markAllRead, dismiss, dismissAll, openNotification } = useNotifications();
  const navigate = useNavigate();
  const { token } = theme.useToken();

  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  const [category, setCategory] = useState('');
  const [readState, setReadState] = useState('all');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');

  // Debounced so a search does not fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async (append = false, from = null) => {
    setLoading(true);
    try {
      const data = await fetchNotifications({
        limit: PAGE_SIZE,
        cursor: from,
        category: category || undefined,
        unreadOnly: readState === 'unread' || undefined,
        q: query || undefined,
      });
      setRows((prev) => {
        if (!append) return data.notifications || [];
        const seen = new Set(prev.map((n) => n.id));
        return [...prev, ...(data.notifications || []).filter((n) => !seen.has(n.id))];
      });
      setCursor(data.nextCursor || null);
      setHasMore(!!data.hasMore);
    } catch (err) {
      antdMessage.error('Could not load notifications');
    } finally {
      setLoading(false);
    }
  }, [category, readState, query]);

  useEffect(() => { load(false, null); }, [load]);

  const grouped = useMemo(() => {
    const out = [];
    let last = null;
    rows.forEach((n) => {
      const bucket = dayBucket(n.createdAt);
      if (bucket !== last) { out.push({ __header: bucket }); last = bucket; }
      out.push(n);
    });
    return out;
  }, [rows]);

  const onRowRead = async (item) => { await markRead(item.id); setRows((p) => p.map((n) => (n.id === item.id ? { ...n, read: true } : n))); };
  const onRowDismiss = async (item) => { await dismiss(item.id); setRows((p) => p.filter((n) => n.id !== item.id)); };

  return (
    <div style={{ padding: 24, maxWidth: 980, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Notifications</Title>
          <Text type="secondary">
            {unread ? `${unread} unread` : 'All caught up'}
            {category && byCategory[category] ? ` · ${byCategory[category]} in ${CATEGORY_LABEL[category]}` : ''}
          </Text>
        </div>
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={() => load(false, null)}>Refresh</Button>
          <Button icon={<SettingOutlined />} onClick={() => navigate('/notifications/settings')}>Settings</Button>
          <Button
            icon={<CheckOutlined />}
            disabled={!unread}
            onClick={async () => { await markAllRead(category || null); load(false, null); }}
          >
            Mark {category ? 'category' : 'all'} read
          </Button>
          <Popconfirm
            title="Clear all notifications?"
            description="They are removed from your inbox. Nothing else is affected."
            okText="Clear" okButtonProps={{ danger: true }}
            onConfirm={async () => { await dismissAll(); load(false, null); }}
          >
            <Button danger icon={<DeleteOutlined />} disabled={!rows.length}>Clear</Button>
          </Popconfirm>
        </Space>
      </div>

      <Card size="small" style={{ marginBottom: 16 }} styles={{ body: { padding: 12 } }}>
        <Space wrap size={12}>
          <Select
            value={category}
            onChange={setCategory}
            options={CATEGORY_OPTIONS}
            style={{ minWidth: 200 }}
          />
          <Segmented
            value={readState}
            onChange={setReadState}
            options={[{ label: 'All', value: 'all' }, { label: 'Unread', value: 'unread' }]}
          />
          <Input.Search
            allowClear
            placeholder="Search title or message"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 280 }}
          />
        </Space>
      </Card>

      <Card styles={{ body: { padding: 0 } }}>
        {loading && !rows.length ? (
          <div style={{ padding: 24 }}><Skeleton active paragraph={{ rows: 6 }} /></div>
        ) : (
          <List
            dataSource={grouped}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  style={{ padding: '48px 0' }}
                  description={query || category || readState === 'unread'
                    ? 'Nothing matches these filters'
                    : 'No notifications yet'}
                />
              ),
            }}
            renderItem={(item) => {
              if (item.__header) {
                return (
                  <div
                    key={`h-${item.__header}`}
                    style={{
                      padding: '7px 20px',
                      background: token.colorFillQuaternary,
                      fontSize: 11,
                      letterSpacing: '.06em',
                      textTransform: 'uppercase',
                      color: token.colorTextTertiary,
                    }}
                  >
                    {item.__header}
                  </div>
                );
              }
              return (
                <List.Item
                  style={{
                    padding: '14px 20px',
                    cursor: 'pointer',
                    background: item.read ? 'transparent' : token.colorPrimaryBg,
                    alignItems: 'flex-start',
                  }}
                  onClick={() => { if (!item.read) onRowRead(item); openNotification(item); }}
                  actions={[
                    !item.read && (
                      <Tooltip title="Mark read" key="r">
                        <Button type="text" size="small" icon={<CheckOutlined />} onClick={(e) => { e.stopPropagation(); onRowRead(item); }} />
                      </Tooltip>
                    ),
                    <Tooltip title="Remove" key="d">
                      <Button type="text" size="small" icon={<DeleteOutlined />} onClick={(e) => { e.stopPropagation(); onRowDismiss(item); }} />
                    </Tooltip>,
                  ].filter(Boolean)}
                >
                  <List.Item.Meta
                    avatar={(
                      <span style={{
                        display: 'inline-flex',
                        width: 34,
                        height: 34,
                        borderRadius: '50%',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `${severityColor(item.severity)}1A`,
                      }}
                      >
                        <NotificationIcon type={item.type} severity={item.severity} />
                      </span>
                    )}
                    title={(
                      <Space size={8} wrap>
                        <Text strong={!item.read}>{item.title}</Text>
                        <Tag bordered={false} style={{ fontSize: 11 }}>{CATEGORY_LABEL[item.category] || item.category}</Tag>
                        {item.severity === 'critical' && <Tag color="error" bordered={false} style={{ fontSize: 11 }}>Critical</Tag>}
                      </Space>
                    )}
                    description={(
                      <Space direction="vertical" size={2}>
                        <Text type="secondary">{item.message || '—'}</Text>
                        <Tooltip title={new Date(item.createdAt).toLocaleString()}>
                          <Text type="secondary" style={{ fontSize: 11 }}>{relativeTime(item.createdAt)}</Text>
                        </Tooltip>
                      </Space>
                    )}
                  />
                </List.Item>
              );
            }}
          />
        )}

        {hasMore && (
          <div style={{ padding: 14, textAlign: 'center', borderTop: `1px solid ${token.colorBorderSecondary}` }}>
            <Button loading={loading} onClick={() => load(true, cursor)}>Load more</Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default NotificationsPage;
